<template>
  <div class="new-budget-container new-budget-page--glass glass-tab-page">
    <BrucdcumHeader
      :breadcrumb="['Company', 'Budget', 'New Budget']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush new-budget-glass-shell">
      <div class="glass-tab-view__content new-budget-page-stack">
        <div class="new-budget-header-block">
          <h2 class="new-budget-title">New Budget</h2>
          <p class="new-budget-subtitle">
            Set up a fiscal-year budget and enter monthly amounts by account
          </p>
        </div>

        <form class="new-budget-form-panel glass-tab-panel--nested" @submit.prevent>
          <div class="glass-tab-panel__row new-budget-form-row--cols-3">
            <div class="new-budget-form-field">
              <LabelDropdown
                label="Start With"
                select-id="new-budget-start-with"
                v-model="startWith"
                flex
                wide
                :searchable="false"
                :options="startWithOptions"
              />
            </div>
            <div class="new-budget-form-field">
              <LabelDropdown
                label="Select fiscal year"
                select-id="new-budget-fiscal-year"
                v-model="selectedFiscalYear"
                flex
                wide
                required
                :searchable="false"
                :options="fiscalYearDropdownOptions"
                @update:model-value="handleFiscalYearChange"
              />
              <div v-if="showNewYearInput" class="new-budget-new-year-block">
                <LabelInputField
                  input-id="new-budget-fiscal-year-input"
                  label="New fiscal year"
                  v-model="newFiscalYear"
                  type="number"
                  flex
                  placeholder="e.g. 2025"
                  @keyup.enter="confirmNewYear"
                />
                <div class="new-budget-new-year-actions">
                  <PrimaryButton label="Confirm" @click="confirmNewYear" />
                  <SecondaryButton label="Cancel" @click="cancelNewYear" />
                </div>
              </div>
            </div>
            <div class="new-budget-form-field">
              <LabelInputField
                input-id="new-budget-name"
                label="Budget Name"
                v-model="budgetName"
                flex
                required
                placeholder="Budget Name"
              />
            </div>
          </div>
        </form>

        <div class="new-budget-table-panel glass-tab-panel--nested">
          <h3 class="new-budget-table-heading">Monthly amounts</h3>
          <div class="new-budget-table-section consultant-glass-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loadingAccounts }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table new-budget-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th v-for="month in months" :key="month">{{ month }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loadingAccounts">
                      <td :colspan="months.length + 1" class="consultant-glass-table-no-records new-budget-no-records">
                        Loading accounts...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td :colspan="months.length + 1" class="consultant-glass-table-no-records new-budget-no-records">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="accounts.length === 0">
                      <td :colspan="months.length + 1" class="consultant-glass-table-no-records new-budget-no-records">
                        No accounts found for the selected account types.
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(account, idx) in accounts"
                        :key="account.id || account.accountName"
                        class="consultant-glass-table-row new-budget-table-row"
                        :style="{ '--nb-row-delay': `${0.03 + Math.min(idx, 12) * 0.02}s` }"
                      >
                        <td class="new-budget-account-name">{{ account.accountName }}</td>
                        <td v-for="month in months" :key="month">
                          <input
                            type="number"
                            :value="getBudgetValue(account.id || account.accountName, month)"
                            class="new-budget-amount-input"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            @input="updateBudgetValue(account.id || account.accountName, month, $event.target.value)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="new-budget-actions-panel glass-tab-panel--nested">
          <div class="new-budget-action-buttons">
            <PrimaryButton label="Save and Close" type="button" />
            <PrimaryButton label="Save and New" type="button" />
            <SecondaryButton label="Cancel" type="button" @click="goToBudgetList" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/NewBudget.css';
import '../styles/new-budget-glass.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const BUDGET_ACCOUNT_TYPES = ['Income', 'Other Income', 'Cost of Goods Sold', 'Expense', 'Other Expense'];

const START_WITH_OPTIONS = Object.freeze([
  { value: 'scratch', label: 'Start from Scratch' },
  { value: 'copy', label: 'Copy from Existing Budget' },
]);

const formatFiscalYearLabel = (year) => {
  const endYear = year + 1;
  return `FY ${year}-${String(endYear).slice(-2)}`;
};

export default {
  name: 'NewBudget',
  components: {
    BrucdcumHeader,
    LabelInputField,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      startWith: 'scratch',
      budgetName: '',
      startWithOptions: START_WITH_OPTIONS,
      months: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ],
      accounts: [],
      loadingAccounts: false,
      error: null,
      budgetData: {},
      selectedFiscalYear: null,
      fiscalYears: [],
      showNewYearInput: false,
      newFiscalYear: null,
    };
  },
  computed: {
    companyId() {
      const storeCompany = this.$store?.state?.companies?.currentCompany;
      if (storeCompany?.id) {
        return storeCompany.id;
      }

      const globalCompany = this.$store?.state?.currentCompany;
      if (globalCompany?.id) {
        return globalCompany.id;
      }

      const routeCompanyId = this.$route?.params?.companyId || this.$route?.params?.id;
      if (routeCompanyId) {
        return routeCompanyId;
      }

      const localCompanyId = localStorage.getItem('currentCompanyId');
      if (localCompanyId) {
        return localCompanyId;
      }

      return null;
    },
    backFallbackRoute() {
      const companyId = this.companyId || this.$route.params.id;
      return `/company/${companyId}/dashboard/company/budget`;
    },
    fiscalYearDropdownOptions() {
      return [
        { value: '', label: 'Select fiscal year', disabled: true },
        ...this.fiscalYears.map((year) => ({
          value: year.value,
          label: year.label,
        })),
        { value: 'create-new', label: '+ Create New Year' },
      ];
    },
  },
  created() {
    this.initializeFiscalYears();
    this.fetchAccounts();
  },
  methods: {
    goToBudgetList() {
      this.$router.push(this.backFallbackRoute);
    },
    async fetchAccounts() {
      if (!this.companyId) {
        this.error = 'Company ID not available';
        this.accounts = [];
        return;
      }

      this.loadingAccounts = true;
      this.error = null;

      try {
        const res = await ApiService.getCompanyAccounts(this.companyId);
        const allAccounts = res.data?.accounts || res.data || [];

        this.accounts = allAccounts.filter((account) => {
          const accountType = account.accountType || '';
          return BUDGET_ACCOUNT_TYPES.includes(accountType);
        });

        this.accounts.sort((a, b) => {
          const typeOrder = {
            Income: 1,
            'Other Income': 2,
            'Cost of Goods Sold': 3,
            Expense: 4,
            'Other Expense': 5,
          };
          const typeA = typeOrder[a.accountType] || 99;
          const typeB = typeOrder[b.accountType] || 99;

          if (typeA !== typeB) {
            return typeA - typeB;
          }
          return (a.accountName || '').localeCompare(b.accountName || '');
        });

        if (this.accounts.length === 0) {
          this.error = 'No accounts found matching budget account types';
        } else {
          this.initializeBudgetData();
        }
      } catch (err) {
        logger.error('Error fetching accounts:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to load accounts';
        this.accounts = [];
      } finally {
        this.loadingAccounts = false;
      }
    },
    initializeBudgetData() {
      const newBudgetData = {};
      this.accounts.forEach((account) => {
        const accountKey = account.id || account.accountName;
        newBudgetData[accountKey] = {};
        this.months.forEach((month) => {
          newBudgetData[accountKey][month] = 0;
        });
      });
      this.budgetData = { ...newBudgetData };
    },
    getBudgetValue(accountKey, month) {
      if (!this.budgetData[accountKey]) {
        return 0;
      }
      return this.budgetData[accountKey][month] || 0;
    },
    updateBudgetValue(accountKey, month, value) {
      const numValue = parseFloat(value) || 0;

      if (!this.budgetData[accountKey]) {
        this.budgetData[accountKey] = {};
      }

      if (this.budgetData[accountKey][month] !== numValue) {
        this.budgetData[accountKey][month] = numValue;
      }
    },
    initializeFiscalYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const fiscalStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;

      const years = [];

      for (let i = 5; i >= 1; i--) {
        const year = fiscalStartYear - i;
        years.push({
          value: year,
          label: formatFiscalYearLabel(year),
        });
      }

      years.push({
        value: fiscalStartYear,
        label: `${formatFiscalYearLabel(fiscalStartYear)} (Current)`,
      });

      for (let i = 1; i <= 4; i++) {
        const year = fiscalStartYear + i;
        years.push({
          value: year,
          label: formatFiscalYearLabel(year),
        });
      }

      this.fiscalYears = years;

      if (years.length > 0) {
        this.selectedFiscalYear = fiscalStartYear;
      }
    },
    handleFiscalYearChange(value) {
      if (value === 'create-new') {
        this.showNewYearInput = true;
        this.newFiscalYear = new Date().getFullYear() + 1;
      } else {
        this.showNewYearInput = false;
        this.newFiscalYear = null;
      }
    },
    confirmNewYear() {
      const year = Number(this.newFiscalYear);
      if (!year || year < 2000 || year > 2100) {
        if (this.$toast?.error) {
          this.$toast.error('Please enter a valid year between 2000 and 2100');
        } else {
          alert('Please enter a valid year between 2000 and 2100');
        }
        return;
      }

      const yearExists = this.fiscalYears.some((y) => y.value === year);

      if (yearExists) {
        if (this.$toast?.error) {
          this.$toast.error('This fiscal year already exists');
        } else {
          alert('This fiscal year already exists');
        }
        return;
      }

      const newYear = {
        value: year,
        label: formatFiscalYearLabel(year),
      };

      this.fiscalYears.push(newYear);
      this.fiscalYears.sort((a, b) => a.value - b.value);

      this.selectedFiscalYear = year;
      this.showNewYearInput = false;
      this.newFiscalYear = null;

      if (this.$toast?.success) {
        this.$toast.success('Fiscal year added successfully');
      } else {
        alert('Fiscal year added successfully');
      }
    },
    cancelNewYear() {
      this.showNewYearInput = false;
      this.newFiscalYear = null;
      this.selectedFiscalYear = this.fiscalYears.length ? this.fiscalYears[this.fiscalYears.length - 1]?.value ?? '' : '';
    },
  },
};
</script>
